// Vollea — Team builder panel (embedded inside LeagueDetail)
// Renders three states: HAS_TEAM, HAS_OUTGOING_INVITE, NO_TEAM (with picker + incoming list).
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { IoSearch, IoArrowForward, IoCloseCircleOutline, IoTrashOutline, IoPersonAdd, IoCheckmark, IoClose, IoTrophy, IoLockClosedOutline } from "react-icons/io5";
import { Card, Avatar, Btn, Input, Empty } from "./ui-vollea";
import { api } from "../lib/api";
import { C, SP, R } from "../lib/theme";

export default function TeamPanel({ leagueId, division, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgOk, setMsgOk] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.get(`/leagues/${leagueId}/my-team`)); }
    catch { setData(null); }
    finally { setLoading(false); }
  }, [leagueId]);

  useEffect(() => { load(); }, [load]);

  const reportOk = (text) => { setMsgOk(true); setMsg(text); };
  const reportErr = (e) => { setMsgOk(false); setMsg(e.message || "Something went wrong"); };

  const invite = async (partner) => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/leagues/${leagueId}/partner/invite`, { partner_id: partner.user_id });
      reportOk(`Invite sent to ${partner.name}.`);
      await load();
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const accept = async (id) => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/partner/invites/${id}/accept`);
      reportOk("Team formed!");
      await load();
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const decline = async (id) => {
    setBusy(true); setMsg("");
    try {
      await api.post(`/partner/invites/${id}/decline`);
      reportOk("Invite declined.");
      await load();
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const cancelOutgoing = async (id) => {
    if (!window.confirm("Cancel this partner invite? You can pick a different partner afterwards.")) return;
    setBusy(true); setMsg("");
    try {
      // Dedicated from-side endpoint — server enforces sender-only + pending-only.
      await api.post(`/partner/invites/${id}/cancel`);
      reportOk("Invite cancelled.");
      await load();
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const leaveTeam = async () => {
    if (!window.confirm("Leave your current team? You can pick a new partner afterwards.")) return;
    setBusy(true); setMsg("");
    try {
      await api.del(`/leagues/${leagueId}/my-team`);
      reportOk("Team disbanded.");
      await load();
      onChanged?.();
    } catch (e) { reportErr(e); }
    finally { setBusy(false); }
  };

  const filteredPartners = useMemo(() => {
    if (!data?.available_partners) return [];
    const ql = q.trim().toLowerCase();
    if (!ql) return data.available_partners;
    return data.available_partners.filter((p) => (p.name || "").toLowerCase().includes(ql));
  }, [data, q]);

  if (loading) return <div style={{ color: C.lime, padding: SP.xl, textAlign: "center" }}>Loading team…</div>;
  if (!data) return null;

  // User isn't a member — organisers viewing a league they don't play in.
  if (!data.division) {
    return (
      <Empty
        icon={<IoTrophy size={36} color={C.textDim} />}
        text="You're not playing in this league. Join with the league code from the My Leagues page to form a team."
        testid="team-panel-non-member"
      />
    );
  }

  return (
    <div data-testid="team-panel">
      {msg ? (
        <div
          data-testid="team-panel-msg"
          style={{
            marginBottom: SP.md,
            background: msgOk ? C.limeTint : "rgba(239,68,68,0.1)",
            border: `1px solid ${msgOk ? C.lime : C.error}`,
            color: msgOk ? C.lime : C.error,
            padding: SP.md, borderRadius: R.md, fontSize: 13, fontWeight: 600,
          }}
        >{msg}</div>
      ) : null}

      {/* HAS TEAM */}
      {data.team ? (
        <Card style={{ border: `1px solid ${C.lime}` }} testid="team-panel-current-team">
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.sm, textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 4 }}>
            <IoTrophy size={12} color={C.lime} /> Your team
          </div>
          <div style={{ color: C.text, fontWeight: 700, fontSize: 18 }}>{data.team.name}</div>
          <div style={{ color: C.textMuted, fontSize: 12, marginTop: 4 }}>Division {data.team.division} · {data.team.played || 0} played, {data.team.wins || 0}W / {data.team.losses || 0}L</div>
          <div style={{ display: "flex", flexDirection: "column", gap: SP.sm, marginTop: SP.md, paddingTop: SP.md, borderTop: `1px solid ${C.divider}` }}>
            {data.team.players.map((p) => (
              <div key={p.user_id} data-testid={`team-player-${p.user_id}`} style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                <Avatar uri={p.photo} name={p.name} size={36} />
                <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{p.name}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: SP.lg }}>
            {data.locked ? (
              <div data-testid="team-locked-banner" style={{ background: "rgba(245,158,11,0.1)", border: `1px solid ${C.warning}`, color: C.warning, padding: SP.md, borderRadius: R.md, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <IoLockClosedOutline size={14} /> Partner is locked — your team has already played matches.
              </div>
            ) : (
              <button
                onClick={leaveTeam}
                disabled={busy}
                data-testid="team-leave-button"
                style={{ height: 44, width: "100%", borderRadius: R.md, border: `1px solid ${C.error}`, color: C.error, fontWeight: 700, fontSize: 13, letterSpacing: 1, textTransform: "uppercase", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, background: "transparent" }}
              >
                <IoTrashOutline size={14} /> Leave team
              </button>
            )}
          </div>
        </Card>
      ) : data.outgoing_invite ? (
        // OUTGOING INVITE
        <Card testid="team-panel-outgoing-invite">
          <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.sm, textTransform: "uppercase" }}>Waiting for response</div>
          <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
            <Avatar uri={data.outgoing_invite.other.photo} name={data.outgoing_invite.other.name} size={40} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>Invited {data.outgoing_invite.other.name}</div>
              <div style={{ color: C.textMuted, fontSize: 12 }}>They'll see this on their dashboard.</div>
            </div>
            <button onClick={() => cancelOutgoing(data.outgoing_invite.invite_id)} disabled={busy} data-testid="team-cancel-outgoing" style={{ padding: "8px 12px", borderRadius: R.sm, background: C.surface3, color: C.text, fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <IoCloseCircleOutline size={14} /> Cancel
            </button>
          </div>
        </Card>
      ) : (
        // NO TEAM — incoming + picker
        <div style={{ display: "flex", flexDirection: "column", gap: SP.md }}>
          {data.incoming_invites?.length > 0 && (
            <div data-testid="team-incoming-invites">
              <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2, marginBottom: SP.sm, textTransform: "uppercase" }}>
                Incoming invites ({data.incoming_invites.length})
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
                {data.incoming_invites.map((inv) => (
                  <Card key={inv.invite_id} testid={`team-incoming-${inv.invite_id}`} style={{ border: `1px solid ${C.lime}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                      <Avatar uri={inv.other.photo} name={inv.other.name} size={40} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{inv.other.name}</div>
                        <div style={{ color: C.textMuted, fontSize: 12 }}>wants to partner up</div>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={() => accept(inv.invite_id)} disabled={busy} data-testid={`team-accept-${inv.invite_id}`} style={{ padding: "8px 12px", borderRadius: R.sm, background: C.lime, color: C.onLime, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                          <IoCheckmark size={14} /> Accept
                        </button>
                        <button onClick={() => decline(inv.invite_id)} disabled={busy} data-testid={`team-decline-${inv.invite_id}`} style={{ padding: "8px 10px", borderRadius: R.sm, background: C.surface3, color: C.text, fontSize: 12 }}>
                          <IoClose size={14} />
                        </button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <Card>
            <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1, marginBottom: SP.sm, textTransform: "uppercase" }}>Find a partner</div>
            <p style={{ color: C.textMuted, fontSize: 13, margin: `0 0 ${SP.md}px` }}>
              Pick someone from your division ({division || data.division}) to start your team. They'll need to accept.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: SP.sm, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: R.md, padding: `0 ${SP.md}px` }}>
              <IoSearch size={16} color={C.textDim} />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search players…"
                testid="team-partner-search"
                style={{ border: "none", background: "transparent", padding: 0, height: 44 }}
              />
            </div>

            <div data-testid="team-partner-list" style={{ marginTop: SP.md, display: "flex", flexDirection: "column", gap: SP.sm }}>
              {filteredPartners.length === 0 ? (
                <div style={{ color: C.textMuted, fontSize: 13, padding: SP.md, textAlign: "center" }}>
                  {q.trim() ? "No players match your search." : "No players currently looking for a partner in this division."}
                </div>
              ) : filteredPartners.map((p) => (
                <div key={p.user_id} data-testid={`team-partner-${p.user_id}`} style={{ display: "flex", alignItems: "center", gap: SP.md, padding: SP.sm, borderRadius: R.md, background: C.surface2, border: `1px solid ${C.border}` }}>
                  <Avatar uri={p.photo} name={p.name} size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ color: C.text, fontWeight: 600, fontSize: 14 }}>{p.name}</div>
                  </div>
                  <button onClick={() => invite(p)} disabled={busy} data-testid={`team-invite-${p.user_id}`} style={{ padding: "6px 12px", borderRadius: R.sm, background: C.lime, color: C.onLime, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                    <IoPersonAdd size={12} /> Invite
                  </button>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      <div style={{ height: SP.lg }} />
      <button onClick={load} disabled={busy} data-testid="team-refresh" style={{ display: "block", margin: "0 auto", color: C.textDim, fontSize: 12, padding: 8 }}>
        <IoArrowForward style={{ verticalAlign: "middle", marginRight: 4 }} size={12} /> Refresh
      </button>
    </div>
  );
}
